import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
    Users,
    Plus,
    Search,
    Eye,
    Edit,
    CheckCircle,
    AlertTriangle,
    Trash2,
    Loader2,
    Mail,
    UserCheck,
    Clock,
    X,
} from 'lucide-react';
import { apiClient, User } from '../services/api';
import MultiSelectFilter from './MultiSelectFilter';
import CreateUserModal from './Modals/CreateUserModal';
import InviteUserModal from './Modals/InviteUserModal';
import TemporaryPasswordModal from './Modals/TemporaryPasswordModal';
import EditUserModal from './Modals/EditUserModal';
import ConfirmDeleteModal from './Modals/ConfirmDeleteModal';
import ConfirmReactivateModal from './Modals/ConfirmReactivateModal';
import ConfirmResendModal from './Modals/ConfirmResendModal';
import UserViewModal from './Modals/UserViewModal';

interface UserManagementProps {
    user: any;
}

const UserManagement: React.FC<UserManagementProps> = ({ user }) => {
    // State management
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [baseTotalUsers, setBaseTotalUsers] = useState(0);
    const [categoryCount, setCategoryCount] = useState({
        totalUsers: 0,
        activeUsers: 0,
        pendingInvites: 0,
        expiredInvites: 0
    });

    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending' | 'expired'>('all');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showReactivateModal, setShowReactivateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isInviting, setIsInviting] = useState(false);
    const [createdUserData, setCreatedUserData] = useState<{
        firstName: string;
        lastName: string;
        email: string;
        temporaryPassword: string;
    } | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [isReactivating, setIsReactivating] = useState(false);
    const [userToReactivate, setUserToReactivate] = useState<User | null>(null);
    const [userToView, setUserToView] = useState<User | null>(null);
    const [showResendModal, setShowResendModal] = useState(false);
    const [userToResend, setUserToResend] = useState<User | null>(null);

    // Load users on component mount and when filters change
    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, roleFilter, statusFilter, currentPage]);

    const loadUsers = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await apiClient.getUsers({
                search: searchTerm,
                page: currentPage,
                limit: pageSize,
                role: roleFilter,
                isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
                invitationStatus: statusFilter === 'pending' || statusFilter === 'expired' ? statusFilter : undefined,
            });

            setUsers(response.data);
            setTotalCount(response.pagination.total);
            if (!searchTerm && roleFilter === 'all' && statusFilter === 'all') {
                setBaseTotalUsers(response.pagination.total);
            }
            setCategoryCount(response.counts);
        } catch (err: any) {
            setError(err.message || 'Failed to load users');
            console.error('Error loading users:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleRoleFilter = (value: string) => {
        setRoleFilter(value);
        setCurrentPage(1);
    };

    const handleStatusFilter = (value: 'all' | 'active' | 'inactive' | 'pending' | 'expired') => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    const getRoleBarClass = (role: string) => {
        switch (role) {
            case 'super_admin': return 'bg-purple-500';
            case 'admin':       return 'bg-blue-500';
            case 'manager':     return 'bg-[#69BE28]';
            case 'compliance':  return 'bg-amber-400';
            default:            return 'bg-gray-300';
        }
    };

    const formatRoleName = (role: string) => {
        const roleNames: { [key: string]: string } = {
            'super_admin': 'Super Admin',
            'admin': 'Admin',
            'manager': 'Manager',
            'compliance': 'Compliance',
            'user': 'User'
        };
        return roleNames[role] || role;
    };

    const formatDate = (dateString: string | Date | null) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const isPendingInvitation = (user: User) => {
        if (!user.invitationToken || !user.invitationExpiry) return false;
        const now = new Date();
        const expiry = new Date(user.invitationExpiry);
        return expiry > now;
    };

    const isExpiredInvitation = (user: User) => {
        if (!user.invitationToken || !user.invitationExpiry) return false;
        const now = new Date();
        const expiry = new Date(user.invitationExpiry);
        return expiry <= now;
    };

    const getUserStatus = (user: User) => {
        if (isPendingInvitation(user)) {
            return { label: 'Invite Pending', barClass: 'bg-amber-400' };
        }
        if (isExpiredInvitation(user)) {
            return { label: 'Invite Expired', barClass: 'bg-orange-400' };
        }
        if (!user.isActive) {
            return { label: 'Inactive', barClass: 'bg-red-500' };
        }
        return { label: 'Active', barClass: 'bg-green-500' };
    };

    const handleCreateUser = async (userData: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    }) => {
        try {
            setIsCreating(true);
            setError(null);

            const response = await apiClient.createUser(userData);

            setShowCreateModal(false);

            // Show temporary password in modal
            if (response.temporaryPassword) {
                setCreatedUserData({
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    email: userData.email,
                    temporaryPassword: response.temporaryPassword,
                });
                setShowPasswordModal(true);
            }

            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to create user');
            throw err;
        } finally {
            setIsCreating(false);
        }
    };

    const handleInviteUser = async (userData: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    }) => {
        try {
            setIsInviting(true);
            setError(null);

            await apiClient.inviteUser(userData);

            toast.success(`Invitation sent successfully to ${userData.email}!`);

            setShowInviteModal(false);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to send invitation');
            throw err;
        } finally {
            setIsInviting(false);
        }
    };

    const handleEditUser = async (userData: {
        firstName: string;
        lastName: string;
        role: string;
    }) => {
        if (!selectedUser) return;

        try {
            setIsUpdating(true);
            setError(null);

            await apiClient.updateUser(selectedUser.id, {
                ...userData,
                email: selectedUser.email, // Email is required by API but not editable
            });

            setShowEditModal(false);
            setSelectedUser(null);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to update user');
            throw err;
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            setIsDeleting(true);
            setError(null);

            await apiClient.deleteUser(userToDelete.id);

            setShowDeleteModal(false);
            setUserToDelete(null);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to deactivate user');
            throw err;
        } finally {
            setIsDeleting(false);
        }
    };

    const handleReactivateUser = async () => {
        if (!userToReactivate) return;

        try {
            setIsReactivating(true);
            setError(null);

            await apiClient.reactivateUser(userToReactivate.id);

            setShowReactivateModal(false);
            setUserToReactivate(null);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to reactivate user');
            throw err;
        } finally {
            setIsReactivating(false);
        }
    };

    const handleResendInvitation = async (user: User) => {
        try {
            setError(null);
            await apiClient.resendInvitation(user.id);
            toast.success(`Invitation resent to ${user.email}!`);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to resend invitation');
            toast.error(err.message || 'Failed to resend invitation');
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="min-h-full" style={{ backgroundColor: '#f3f5f1' }}>
        <div className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-8">

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-2 mb-5">
                <button
                    onClick={() => setShowInviteModal(true)}
                    className="inline-flex items-center px-3 py-2 text-gray-700 bg-white text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <Mail className="w-4 h-4 mr-2" />
                    Invite User
                </button>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-3 py-2 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity shadow-sm"
                    style={{ backgroundColor: '#69BE28' }}
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                </button>
            </div>

            {/* Stat Tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <button
                    type="button"
                    onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
                    className={`group relative bg-white rounded-xl border shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 cursor-pointer hover:shadow-md transition-all text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${statusFilter === 'all' && roleFilter === 'all' && !searchTerm ? 'border-gray-900' : 'border-gray-200/80'}`}
                >
                    <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${statusFilter === 'all' && roleFilter === 'all' && !searchTerm ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                    <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <Users className="w-4 h-4" style={{ color: '#69BE28' }} />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 leading-none">{categoryCount.totalUsers}</p>
                    <p className="text-xs text-gray-500 mt-1.5">Total Users</p>
                    <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100">
                        All users in your organisation.
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => { setStatusFilter('active'); setCurrentPage(1); }}
                    className={`group relative bg-white rounded-xl border shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 cursor-pointer hover:shadow-md transition-all text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${statusFilter === 'active' ? 'border-gray-900' : 'border-gray-200/80'}`}
                >
                    <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${statusFilter === 'active' ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                    <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: 'rgba(22,163,74,0.1)' }}>
                        <UserCheck className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 leading-none">{categoryCount.activeUsers}</p>
                    <p className="text-xs text-gray-500 mt-1.5">Active Users</p>
                    <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100">
                        Users with active accounts who can log in.
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => { handleStatusFilter('pending'); }}
                    className={`group relative bg-white rounded-xl border shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 cursor-pointer hover:shadow-md transition-all text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${statusFilter === 'pending' ? 'border-gray-900' : 'border-gray-200/80'}`}
                >
                    <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${statusFilter === 'pending' ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                    <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: 'rgba(217,119,6,0.1)' }}>
                        <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 leading-none">{categoryCount.pendingInvites}</p>
                    <p className="text-xs text-gray-500 mt-1.5">Pending Invites</p>
                    <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100">
                        Users invited but not yet activated.
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => { handleStatusFilter('expired'); }}
                    className={`group relative bg-white rounded-xl border shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 cursor-pointer hover:shadow-md transition-all text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${statusFilter === 'expired' ? 'border-gray-900' : 'border-gray-200/80'}`}
                >
                    <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${statusFilter === 'expired' ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
                    <div className="p-1.5 rounded-lg w-fit mb-3" style={{ backgroundColor: 'rgba(234,88,12,0.1)' }}>
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 leading-none">{categoryCount.expiredInvites}</p>
                    <p className="text-xs text-gray-500 mt-1.5">Expired Invites</p>
                    <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100">
                        Invitations that expired before the user activated their account.
                    </span>
                </button>
            </div>

            {/* Filters */}
            <div className="mb-5 bg-white p-4 rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>
                    <div className="sm:w-48">
                        <MultiSelectFilter
                            label="Role"
                            allLabel="All Roles"
                            options={[
                                { value: 'admin', label: 'Admin' },
                                { value: 'manager', label: 'Manager' },
                                { value: 'compliance', label: 'Compliance' },
                                { value: 'user', label: 'User' },
                            ]}
                            values={roleFilter === 'all' ? [] : [roleFilter]}
                            onApply={(values) => handleRoleFilter(values[values.length - 1] ?? 'all')}
                        />
                    </div>
                    <div className="sm:w-48">
                        <MultiSelectFilter
                            label="User status"
                            allLabel="All Users"
                            options={[
                                { value: 'active', label: 'Active Users' },
                                { value: 'inactive', label: 'Inactive Users' },
                                { value: 'pending', label: 'Pending Invites' },
                                { value: 'expired', label: 'Expired Invites' },
                            ]}
                            values={statusFilter === 'all' ? [] : [statusFilter]}
                            onApply={(values) => handleStatusFilter((values[values.length - 1] ?? 'all') as any)}
                        />
                    </div>
                </div>
            </div>

            {/* Active filter chips */}
            {(() => {
                const chips: { key: string; label: string; onRemove: () => void }[] = [];

                if (searchTerm) chips.push({
                    key: 'search',
                    label: `Search: "${searchTerm}"`,
                    onRemove: () => handleSearch(''),
                });

                if (roleFilter !== 'all') chips.push({
                    key: 'role',
                    label: `Role: ${formatRoleName(roleFilter)}`,
                    onRemove: () => handleRoleFilter('all'),
                });

                if (statusFilter !== 'all') chips.push({
                    key: 'status',
                    label: statusFilter === 'active' ? 'Active Users only'
                        : statusFilter === 'inactive' ? 'Inactive Users only'
                        : statusFilter === 'pending' ? 'Pending Invites only'
                        : 'Expired Invites only',
                    onRemove: () => handleStatusFilter('all'),
                });

                if (chips.length === 0) return null;

                return (
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        {chips.map(chip => (
                            <span key={chip.key} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-2.5 pr-1.5 text-sm font-medium text-gray-700 shadow-[0_1px_1px_rgba(15,23,42,0.03)]">
                                {chip.label}
                                <button
                                    onClick={chip.onRemove}
                                    className="relative ml-0.5 rounded-full p-1 text-gray-500 transition-colors before:absolute before:-inset-1.5 hover:bg-[#69BE28]/10 hover:text-[#4a8a1c] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-1"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </span>
                        ))}
                        {chips.length >= 2 && (
                            <button
                                onClick={() => { handleSearch(''); handleRoleFilter('all'); handleStatusFilter('all'); }}
                                className="text-sm text-gray-500 hover:text-gray-700 underline"
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* Error Display */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
                        <span className="text-sm text-red-800">{error}</span>
                    </div>
                </div>
            )}

            {/* Users Table */}
            <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#69BE28]/10">
                            <Users className="h-4 w-4 text-[#4a8a1c]" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">User register</h3>
                            <p className="mt-0.5 text-xs text-gray-500">
                                {baseTotalUsers > totalCount
                                    ? `${totalCount} matching of ${baseTotalUsers} users`
                                    : `${totalCount} ${totalCount === 1 ? 'user' : 'users'}`}
                            </p>
                        </div>
                    </div>
                </div>
                {isLoading ? (
                    <div className="p-8 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#69BE28' }} />
                        <p className="mt-2 text-sm text-gray-600">Loading users...</p>
                    </div>
                ) : users.length === 0 ? (
                    <div className="p-8 text-center">
                        <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <h3 className="text-sm font-medium text-gray-900">No users found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating your first user.'}
                        </p>
                    </div>
                ) : (
                    <div>
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50/95 backdrop-blur-sm">
                                <tr>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Last Login</th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                                    <th className="px-4 py-3.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {users.map((userData) => {
                                    const status = getUserStatus(userData);

                                    return (
                                        <tr key={userData.id} className="hover:bg-gray-50/60 transition-colors">
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{userData.firstName} {userData.lastName}</div>
                                                <div className="text-xs text-gray-500">{userData.email}</div>
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                    <span className={`h-4 w-[3px] rounded-full ${getRoleBarClass(userData.role)}`} aria-hidden="true" />
                                                    {formatRoleName(userData.role)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                    <span className={`h-4 w-[3px] rounded-full ${status.barClass}`} aria-hidden="true" />
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(userData.lastLogin)}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(userData.createdAt)}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <button
                                                        onClick={() => { setUserToView(userData); setShowViewModal(true); }}
                                                        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                        title="View User Details"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => { setSelectedUser(userData); setShowEditModal(true); }}
                                                        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                        title="Edit User"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    {userData.id !== user.id && (
                                                        isPendingInvitation(userData) ? null
                                                        : isExpiredInvitation(userData) ? (
                                                            <div className="group/tip relative inline-flex">
                                                                <button
                                                                    onClick={() => { setUserToResend(userData); setShowResendModal(true); }}
                                                                    className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-amber-50 hover:text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-600"
                                                                >
                                                                    <Mail className="w-4 h-4" />
                                                                </button>
                                                                <span className="pointer-events-none absolute bottom-full right-0 z-[100] mb-1.5 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100">Resend Invitation</span>
                                                            </div>
                                                        ) : userData.isActive ? (
                                                            <div className="group/tip relative inline-flex">
                                                                <button
                                                                    onClick={() => { setUserToDelete(userData); setShowDeleteModal(true); }}
                                                                    className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-600"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                                <span className="pointer-events-none absolute bottom-full right-0 z-[100] mb-1.5 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100">Deactivate User</span>
                                                            </div>
                                                        ) : (
                                                            <div className="group/tip relative inline-flex">
                                                                <button
                                                                    onClick={() => { setUserToReactivate(userData); setShowReactivateModal(true); }}
                                                                    className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                                                >
                                                                    <CheckCircle className="w-4 h-4" />
                                                                </button>
                                                                <span className="pointer-events-none absolute bottom-full right-0 z-[100] mb-1.5 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100">Reactivate User</span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6 flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

        </div>

            {/* Create User Modal */}
            <CreateUserModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSubmit={handleCreateUser}
                isCreating={isCreating}
            />

            {/* Invite User Modal */}
            <InviteUserModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                onSubmit={handleInviteUser}
                isInviting={isInviting}
            />

            {/* Temporary Password Modal */}
            {createdUserData && (
                <TemporaryPasswordModal
                    isOpen={showPasswordModal}
                    onClose={() => {
                        setShowPasswordModal(false);
                        setCreatedUserData(null);
                    }}
                    userData={{
                        firstName: createdUserData.firstName,
                        lastName: createdUserData.lastName,
                        email: createdUserData.email,
                    }}
                    temporaryPassword={createdUserData.temporaryPassword}
                />
            )}

            {/* Edit User Modal */}
            <EditUserModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                }}
                onSubmit={handleEditUser}
                isUpdating={isUpdating}
                user={selectedUser}
                currentUserId={user.id}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                }}
                onConfirm={handleDeleteUser}
                isDeleting={isDeleting}
                user={userToDelete ? {
                    firstName: userToDelete.firstName,
                    lastName: userToDelete.lastName,
                    email: userToDelete.email
                } : null}
            />

            {/* Reactivate Confirmation Modal */}
            <ConfirmReactivateModal
                isOpen={showReactivateModal}
                onClose={() => {
                    setShowReactivateModal(false);
                    setUserToReactivate(null);
                }}
                onConfirm={handleReactivateUser}
                isReactivating={isReactivating}
                user={userToReactivate ? {
                    firstName: userToReactivate.firstName,
                    lastName: userToReactivate.lastName,
                    email: userToReactivate.email,
                    role: userToReactivate.role
                } : null}
            />

            <ConfirmResendModal
                isOpen={showResendModal}
                onClose={() => { setShowResendModal(false); setUserToResend(null); }}
                onConfirm={async () => {
                    if (userToResend) await handleResendInvitation(userToResend);
                    setShowResendModal(false);
                    setUserToResend(null);
                }}
                user={userToResend ? {
                    firstName: userToResend.firstName,
                    lastName: userToResend.lastName,
                    email: userToResend.email,
                } : null}
            />

            {/* View User Details Modal */}
            <UserViewModal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setUserToView(null);
                }}
                user={userToView}
                currentUserId={user.id}
                onEdit={() => {
                    setSelectedUser(userToView);
                    setShowEditModal(true);
                }}
                onDeactivate={() => {
                    setUserToDelete(userToView);
                    setShowDeleteModal(true);
                }}
                onReactivate={() => {
                    setUserToReactivate(userToView);
                    setShowReactivateModal(true);
                }}
                onRefresh={loadUsers}
            />
        </div>
    );
};

export default UserManagement;